import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { invoicesAPI, clientsAPI, quickbooksAPI, invoiceEmailAPI, brandingAPI } from '../services/api';
import BillingRunsTab from '../components/billing/BillingRunsTab';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Receipt, Plus, Eye, Trash, Printer, PaperPlaneTilt, CheckCircle, XCircle,
  CurrencyDollar, Clock, CalendarBlank, LinkSimple, LinkBreak, CloudArrowUp, EnvelopeSimple
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800',
  void: 'bg-red-100 text-red-800'
};

const InvoicesPage = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get('tab') === 'runs' ? 'runs' : 'list');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const printRef = useRef(null);

  const [createForm, setCreateForm] = useState({
    client_id: '',
    start_date: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
    notes: ''
  });

  // QuickBooks integration state
  const [qbStatus, setQbStatus] = useState({ connected: false });
  // Invoice branding (per-office) for the in-app HTML invoice preview
  const [branding, setBranding] = useState({
    invoice_company_name: null,
    invoice_company_address: null,
    has_logo: false,
    logo_url: null,
  });
  const [qbConnecting, setQbConnecting] = useState(false);
  const [qbPushingId, setQbPushingId] = useState(null);

  // Email state
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  useEffect(() => { loadData(); loadQbStatus(); }, []);

  // Load office branding so the in-app invoice preview matches the PDF/email
  useEffect(() => {
    const oid = user?.office_id;
    if (!oid) return;
    brandingAPI.get(oid)
      .then(res => setBranding({
        invoice_company_name: res.data.invoice_company_name || null,
        invoice_company_address: res.data.invoice_company_address || null,
        has_logo: !!res.data.has_logo,
        logo_url: res.data.logo_url || null,
      }))
      .catch(() => {});
  }, [user?.office_id]);

  // Handle ?qb=success|error from OAuth callback redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const qb = params.get('qb');
    if (qb === 'success') {
      toast.success('QuickBooks connected successfully');
      loadQbStatus();
      // Clean the query string
      window.history.replaceState({}, '', '/invoices');
    } else if (qb === 'error') {
      toast.error(`QuickBooks connection failed: ${params.get('msg') || 'Unknown error'}`);
      window.history.replaceState({}, '', '/invoices');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadQbStatus = async () => {
    try {
      const res = await quickbooksAPI.status();
      setQbStatus(res.data);
    } catch (e) { /* ignore — user may lack perms */ }
  };

  const handleConnectQuickBooks = async () => {
    setQbConnecting(true);
    try {
      const res = await quickbooksAPI.connect();
      window.location.href = res.data.authorize_url;
    } catch (e) {
      toast.error('Failed to start QuickBooks connection');
      setQbConnecting(false);
    }
  };

  const handleDisconnectQuickBooks = async () => {
    try {
      await quickbooksAPI.disconnect();
      toast.success('QuickBooks disconnected');
      loadQbStatus();
    } catch (e) { toast.error('Failed to disconnect QuickBooks'); }
  };

  const handlePushToQuickBooks = async (invoiceId) => {
    setQbPushingId(invoiceId);
    try {
      const res = await quickbooksAPI.pushInvoice(invoiceId);
      toast.success(`Pushed to QuickBooks (QBO Invoice #${res.data.qbo_invoice_id})`);
      if (viewingInvoice?.id === invoiceId) {
        setViewingInvoice({
          ...viewingInvoice,
          qbo_invoice_id: res.data.qbo_invoice_id,
          qbo_synced_at: new Date().toISOString()
        });
      }
      loadData();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to push invoice to QuickBooks';
      toast.error(msg);
    } finally { setQbPushingId(null); }
  };

  const openEmailDialog = () => {
    if (!viewingInvoice) return;
    const client = clients.find(c => c.id === viewingInvoice.client_id);
    setEmailRecipient(client?.email || '');
    setIsEmailOpen(true);
  };

  const handleSendEmail = async () => {
    if (!viewingInvoice) return;
    if (!emailRecipient || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailRecipient.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }
    setEmailSending(true);
    try {
      const res = await invoiceEmailAPI.send(viewingInvoice.id, { recipient_email: emailRecipient.trim() });
      toast.success(`Invoice emailed to ${res.data.recipient}`);
      setIsEmailOpen(false);
      setViewingInvoice({
        ...viewingInvoice,
        emailed_to: res.data.recipient,
        emailed_at: new Date().toISOString(),
        status: viewingInvoice.status === 'draft' ? 'sent' : viewingInvoice.status
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invoice email');
    } finally { setEmailSending(false); }
  };

  const loadData = async () => {
    try {
      const [invRes, clientsRes] = await Promise.all([
        invoicesAPI.getAll({}),
        clientsAPI.getAll()
      ]);
      setInvoices(invRes.data);
      setClients(clientsRes.data);
    } catch (error) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.client_id) { toast.error('Select a client'); return; }
    try {
      const res = await invoicesAPI.create(createForm);
      toast.success(`Invoice ${res.data.invoice_number} created`);
      setIsCreateOpen(false);
      setCreateForm({ client_id: '', start_date: format(subDays(new Date(), 30), 'yyyy-MM-dd'), end_date: format(new Date(), 'yyyy-MM-dd'), notes: '' });
      loadData();
      setViewingInvoice(res.data);
    } catch (error) { toast.error('Failed to create invoice'); }
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      await invoicesAPI.updateStatus(invoiceId, newStatus);
      toast.success(`Invoice marked as ${newStatus}`);
      if (viewingInvoice?.id === invoiceId) {
        setViewingInvoice({ ...viewingInvoice, status: newStatus });
      }
      loadData();
    } catch (error) { toast.error('Failed to update status'); }
  };

  const handleDelete = async (invoiceId) => {
    try {
      await invoicesAPI.delete(invoiceId);
      toast.success('Invoice deleted');
      loadData();
    } catch (error) { toast.error('Failed to delete invoice'); }
  };

  const handleViewInvoice = async (invoiceId) => {
    try {
      const res = await invoicesAPI.getById(invoiceId);
      setViewingInvoice(res.data);
    } catch (error) { toast.error('Failed to load invoice'); }
  };

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html><head><title>Invoice ${viewingInvoice.invoice_number}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', system-ui, sans-serif; color: #1a1a1a; padding: 40px; max-width: 800px; margin: 0 auto; }
        .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; border-bottom: 3px solid #1a472a; padding-bottom: 20px; }
        .brand { font-size: 24px; font-weight: 700; color: #1a472a; }
        .brand-sub { font-size: 12px; color: #666; }
        .inv-number { font-size: 28px; font-weight: 700; color: #1a472a; text-align: right; }
        .inv-status { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; text-transform: uppercase; }
        .status-draft { background: #f0f0f0; color: #666; }
        .status-sent { background: #dbeafe; color: #1d4ed8; }
        .status-paid { background: #dcfce7; color: #16a34a; }
        .status-void { background: #fde2e2; color: #dc2626; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 30px; }
        .info-box h3 { font-size: 11px; text-transform: uppercase; letter-spacing: 1.5px; color: #888; margin-bottom: 8px; }
        .info-box p { font-size: 14px; line-height: 1.6; }
        .rates { display: flex; gap: 20px; margin-bottom: 30px; }
        .rate-card { flex: 1; padding: 12px; background: #f8f9fa; border-radius: 8px; text-align: center; }
        .rate-label { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #888; }
        .rate-value { font-size: 20px; font-weight: 700; color: #1a472a; margin-top: 4px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
        th { background: #1a472a; color: white; padding: 10px 12px; text-align: left; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; }
        th:last-child, td:last-child { text-align: right; }
        td { padding: 10px 12px; border-bottom: 1px solid #eee; font-size: 13px; }
        tr:nth-child(even) { background: #fafafa; }
        .totals { display: flex; justify-content: flex-end; margin-bottom: 30px; }
        .totals-box { background: #f8f9fa; border: 2px solid #1a472a; border-radius: 8px; padding: 20px 30px; min-width: 280px; }
        .total-row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 14px; }
        .total-row.grand { border-top: 2px solid #1a472a; margin-top: 8px; padding-top: 12px; font-size: 18px; font-weight: 700; color: #1a472a; }
        .notes { background: #fffbe6; border: 1px solid #fde68a; border-radius: 8px; padding: 16px; margin-bottom: 30px; }
        .notes h3 { font-size: 12px; text-transform: uppercase; color: #888; margin-bottom: 8px; }
        .footer { text-align: center; color: #aaa; font-size: 11px; border-top: 1px solid #eee; padding-top: 20px; }
        @media print { body { padding: 20px; } }
      </style></head><body>${content.innerHTML}</body></html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const filteredInvoices = invoices.filter(inv => {
    if (filterStatus !== 'all' && inv.status !== filterStatus) return false;
    if (filterClient !== 'all' && inv.client_id !== filterClient) return false;
    return true;
  });

  if (!['supervisor', 'admin', 'director', 'super_admin', 'client'].includes(user?.role)) {
    return <div className="flex flex-col items-center justify-center h-64"><Receipt weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" /><h3 className="font-heading text-lg">Access Restricted</h3></div>;
  }
  const isClient = user?.role === 'client';

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;

  return (
    <div className="space-y-6 fade-in" data-testid="invoices-page">
      {/* QuickBooks Connect Banner — supervisors only */}
      {!isClient && (
      <Card className={`rounded-2xl border ${qbStatus.connected ? 'border-emerald-200 bg-emerald-50/60' : 'border-border bg-muted/30'}`} data-testid="qb-banner">
        <CardContent className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${qbStatus.connected ? 'bg-emerald-100 text-emerald-700' : 'bg-muted text-muted-foreground'}`}>
              <CloudArrowUp weight="duotone" className="w-6 h-6" />
            </div>
            <div>
              <p className="font-medium text-sm">
                QuickBooks Online
                {qbStatus.connected && (
                  <span className="ml-2 text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold uppercase tracking-wide">
                    {qbStatus.environment || 'connected'}
                  </span>
                )}
              </p>
              {qbStatus.connected ? (
                <p className="text-xs text-muted-foreground mt-0.5">
                  Connected to QBO company <span className="font-mono">{qbStatus.realm_id}</span>
                  {qbStatus.connected_at && ` · since ${format(new Date(qbStatus.connected_at), 'MMM d, yyyy')}`}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mt-0.5">
                  Connect QuickBooks Online to push invoices directly from CareGrid.
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {qbStatus.connected ? (
              <Button variant="outline" size="sm" className="rounded-xl gap-1" onClick={handleDisconnectQuickBooks} data-testid="qb-disconnect-btn">
                <LinkBreak weight="bold" className="w-3.5 h-3.5" /> Disconnect
              </Button>
            ) : (
              <Button size="sm" className="rounded-xl gap-1" onClick={handleConnectQuickBooks} disabled={qbConnecting} data-testid="qb-connect-btn">
                <LinkSimple weight="bold" className="w-3.5 h-3.5" />
                {qbConnecting ? 'Opening…' : 'Connect QuickBooks'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      )}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold">{isClient ? 'My Invoices' : 'Invoices'}</h1>
          <p className="text-muted-foreground mt-1">{isClient ? 'View & download invoices from your home care agency' : 'Generate and manage client invoices'}</p>
        </div>
        {!isClient && (
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl gap-2" data-testid="create-invoice-btn">
              <Plus weight="bold" className="w-4 h-4" /> Generate Invoice
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Generate Invoice</DialogTitle>
              <DialogDescription>Create an invoice from completed visits</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select value={createForm.client_id} onValueChange={(v) => setCreateForm({ ...createForm, client_id: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="invoice-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input type="date" value={createForm.start_date} onChange={(e) => setCreateForm({ ...createForm, start_date: e.target.value })} className="rounded-xl" data-testid="invoice-start-date" />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input type="date" value={createForm.end_date} onChange={(e) => setCreateForm({ ...createForm, end_date: e.target.value })} className="rounded-xl" data-testid="invoice-end-date" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea value={createForm.notes} onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })} placeholder="Payment terms, special notes..." rows={3} className="rounded-xl" data-testid="invoice-notes-input" />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                <Button type="submit" className="flex-1 rounded-xl" data-testid="generate-invoice-btn">Generate</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {!isClient && (
        <div className="flex gap-1 rounded-xl bg-muted p-1 w-fit" data-testid="invoices-tabs">
          <button onClick={() => setTab('list')} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === 'list' ? 'bg-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-invoice-list">All Invoices</button>
          <button onClick={() => setTab('runs')} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === 'runs' ? 'bg-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-billing-runs">Weekly Billing Runs</button>
        </div>
      )}

      {tab === 'runs' && !isClient ? <BillingRunsTab kind="invoice" /> : (<>
      {/* Filters */}
      <Card className="rounded-2xl border border-border">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="rounded-xl" data-testid="invoice-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="void">Void</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Select value={filterClient} onValueChange={setFilterClient}>
                <SelectTrigger className="rounded-xl" data-testid="invoice-client-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoice View Dialog */}
      <Dialog open={!!viewingInvoice} onOpenChange={(open) => { if (!open) setViewingInvoice(null); }}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          {viewingInvoice && (
            <>
              <div className="flex items-center justify-between mb-4">
                <DialogTitle className="font-heading text-xl">{viewingInvoice.invoice_number}</DialogTitle>
                <div className="flex gap-2">
                  {viewingInvoice.status === 'draft' && (
                    <Button size="sm" variant="outline" className="rounded-xl gap-1" onClick={() => handleStatusChange(viewingInvoice.id, 'sent')} data-testid="mark-sent-btn">
                      <PaperPlaneTilt weight="bold" className="w-3 h-3" /> Mark Sent
                    </Button>
                  )}
                  {(viewingInvoice.status === 'draft' || viewingInvoice.status === 'sent') && (
                    <Button size="sm" className="rounded-xl gap-1 bg-green-600 hover:bg-green-700" onClick={() => handleStatusChange(viewingInvoice.id, 'paid')} data-testid="mark-paid-btn">
                      <CheckCircle weight="bold" className="w-3 h-3" /> Mark Paid
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="rounded-xl gap-1" onClick={handlePrint} data-testid="print-invoice-btn">
                    <Printer weight="bold" className="w-3 h-3" /> Print
                  </Button>
                  <Button size="sm" className="rounded-xl gap-1 bg-blue-600 hover:bg-blue-700" onClick={openEmailDialog} data-testid="email-invoice-btn">
                    <EnvelopeSimple weight="bold" className="w-3 h-3" />
                    {viewingInvoice.emailed_at ? 'Resend Email' : 'Email to Client'}
                  </Button>
                  {qbStatus.connected && (
                    viewingInvoice.qbo_invoice_id ? (
                      <Button size="sm" variant="outline" className="rounded-xl gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50" disabled data-testid="qb-synced-badge">
                        <CheckCircle weight="bold" className="w-3 h-3" /> Synced to QB #{viewingInvoice.qbo_invoice_id}
                      </Button>
                    ) : (
                      <Button size="sm" className="rounded-xl gap-1 bg-[#2CA01C] hover:bg-[#248A17]" onClick={() => handlePushToQuickBooks(viewingInvoice.id)} disabled={qbPushingId === viewingInvoice.id} data-testid="push-to-qb-btn">
                        <CloudArrowUp weight="bold" className="w-3 h-3" />
                        {qbPushingId === viewingInvoice.id ? 'Pushing…' : 'Push to QuickBooks'}
                      </Button>
                    )
                  )}
                </div>
              </div>

              {/* Printable Invoice Content */}
              <div ref={printRef}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '30px', borderBottom: '3px solid #1a472a', paddingBottom: '15px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                    {branding.logo_url && (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}`}
                        alt="Logo"
                        style={{ maxHeight: '64px', maxWidth: '180px', objectFit: 'contain' }}
                      />
                    )}
                    <div>
                      <div style={{ fontSize: '22px', fontWeight: 700, color: '#1a472a' }}>{branding.invoice_company_name || 'CareGrid'}</div>
                      {branding.invoice_company_address ? (
                        <div style={{ fontSize: '12px', color: '#666', whiteSpace: 'pre-line' }}>{branding.invoice_company_address}</div>
                      ) : (
                        <div style={{ fontSize: '12px', color: '#666' }}>Home Care Services</div>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '24px', fontWeight: 700, color: '#1a472a' }}>{viewingInvoice.invoice_number}</div>
                    <div style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', background: viewingInvoice.status === 'paid' ? '#dcfce7' : viewingInvoice.status === 'sent' ? '#dbeafe' : '#f0f0f0', color: viewingInvoice.status === 'paid' ? '#16a34a' : viewingInvoice.status === 'sent' ? '#1d4ed8' : '#666' }}>
                      {viewingInvoice.status}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                  <div>
                    <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#888', marginBottom: '6px' }}>Bill To</div>
                    <p style={{ fontSize: '16px', fontWeight: 600 }}>{viewingInvoice.client_name}</p>
                    {viewingInvoice.client_address && <p style={{ fontSize: '13px', color: '#666' }}>{viewingInvoice.client_address}</p>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#888', marginBottom: '6px' }}>Invoice Details</div>
                    <p style={{ fontSize: '13px' }}>Date: {format(new Date(viewingInvoice.created_at), 'MMM d, yyyy')}</p>
                    <p style={{ fontSize: '13px' }}>Period: {viewingInvoice.start_date} to {viewingInvoice.end_date}</p>
                    <p style={{ fontSize: '13px' }}>Created by: {viewingInvoice.created_by_name}</p>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ flex: 1, padding: '10px', background: '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: '#888' }}>Standard</div>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#1a472a' }}>${viewingInvoice.standard_rate?.toFixed(2)}/hr</div>
                  </div>
                  <div style={{ flex: 1, padding: '10px', background: '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: '#888' }}>Overtime</div>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#c2410c' }}>${viewingInvoice.overtime_rate?.toFixed(2)}/hr</div>
                  </div>
                  <div style={{ flex: 1, padding: '10px', background: '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: '#888' }}>Weekend</div>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#7c3aed' }}>${viewingInvoice.weekend_rate?.toFixed(2)}/hr</div>
                  </div>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px' }}>
                  <thead>
                    <tr style={{ background: '#1a472a', color: 'white' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase' }}>Date</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase' }}>Caregiver</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase' }}>Check In/Out</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', textTransform: 'uppercase' }}>Hours</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', textTransform: 'uppercase' }}>Rate</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: '11px', textTransform: 'uppercase' }}>Type</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', textTransform: 'uppercase' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewingInvoice.line_items?.map((item, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#fafafa', borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '8px 12px', fontSize: '12px' }}>{item.date} {item.time}</td>
                        <td style={{ padding: '8px 12px', fontSize: '12px' }}>{item.caregiver_name}</td>
                        <td style={{ padding: '8px 12px', fontSize: '11px', color: '#666' }}>
                          {item.check_in ? format(new Date(item.check_in), 'h:mm a') : '-'} / {item.check_out ? format(new Date(item.check_out), 'h:mm a') : '-'}
                        </td>
                        <td style={{ padding: '8px 12px', fontSize: '12px', textAlign: 'right' }}>{item.hours}</td>
                        <td style={{ padding: '8px 12px', fontSize: '12px', textAlign: 'right' }}>${item.rate.toFixed(2)}</td>
                        <td style={{ padding: '8px 12px', fontSize: '11px', textAlign: 'center', textTransform: 'capitalize' }}>{item.rate_type}</td>
                        <td style={{ padding: '8px 12px', fontSize: '12px', textAlign: 'right', fontWeight: 600 }}>${item.amount.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
                  <div style={{ background: '#f8f9fa', border: '2px solid #1a472a', borderRadius: '8px', padding: '16px 24px', minWidth: '260px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px' }}>
                      <span>Total Visits:</span><span>{viewingInvoice.total_visits}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px' }}>
                      <span>Total Hours:</span><span>{viewingInvoice.total_hours}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0', marginTop: '8px', borderTop: '2px solid #1a472a', fontSize: '18px', fontWeight: 700, color: '#1a472a' }}>
                      <span>Total Due:</span><span>${viewingInvoice.total_amount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {viewingInvoice.notes && (
                  <div style={{ background: '#fffbe6', border: '1px solid #fde68a', borderRadius: '8px', padding: '12px', marginBottom: '20px' }}>
                    <div style={{ fontSize: '11px', textTransform: 'uppercase', color: '#888', marginBottom: '6px' }}>Notes</div>
                    <p style={{ fontSize: '13px' }}>{viewingInvoice.notes}</p>
                  </div>
                )}

                <div style={{ textAlign: 'center', color: '#aaa', fontSize: '11px', borderTop: '1px solid #eee', paddingTop: '16px' }}>
                  {branding.invoice_company_name || 'CareGrid Home Care Services'} &middot; Generated {format(new Date(viewingInvoice.created_at), 'MMMM d, yyyy')}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Invoice Dialog */}
      <Dialog open={isEmailOpen} onOpenChange={setIsEmailOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Email Invoice to Client</DialogTitle>
            <DialogDescription>
              {viewingInvoice && (
                <>Sending <strong>{viewingInvoice.invoice_number}</strong> as a PDF attachment.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Recipient email *</Label>
              <Input
                type="email"
                value={emailRecipient}
                onChange={(e) => setEmailRecipient(e.target.value)}
                placeholder="client@example.com"
                className="rounded-xl"
                data-testid="email-recipient-input"
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                Default pulled from the client's profile. You can override it for this send.
              </p>
            </div>
            <div className="rounded-xl bg-muted/40 p-3 text-xs text-muted-foreground">
              <p><strong>From:</strong> CareGrid &lt;onboarding@resend.dev&gt;</p>
              <p className="mt-1"><strong>Subject:</strong> Invoice {viewingInvoice?.invoice_number} from {branding.invoice_company_name || 'CareGrid'}</p>
              <p className="mt-1"><strong>Attached:</strong> invoice_{viewingInvoice?.invoice_number}.pdf</p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setIsEmailOpen(false)} disabled={emailSending}>Cancel</Button>
              <Button className="flex-1 rounded-xl bg-blue-600 hover:bg-blue-700 gap-2" onClick={handleSendEmail} disabled={emailSending} data-testid="confirm-email-btn">
                <EnvelopeSimple weight="bold" className="w-4 h-4" />
                {emailSending ? 'Sending...' : 'Send Invoice'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoices List */}
      {filteredInvoices.length === 0 ? (
        <Card className="rounded-2xl border border-border">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Receipt weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" />
            <h3 className="font-heading text-lg font-medium">No invoices found</h3>
            <p className="text-muted-foreground text-sm mt-1">Generate your first invoice from billing data</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredInvoices.map(inv => (
            <Card key={inv.id} className="rounded-2xl border border-border hover:border-border/80 hover:shadow-sm transition-all duration-200" data-testid={`invoice-card-${inv.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center shrink-0">
                      <Receipt weight="duotone" className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-heading font-semibold">{inv.invoice_number}</p>
                        <Badge className={`${statusColors[inv.status]} rounded-full text-xs`}>{inv.status}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{inv.client_name}</p>
                      <p className="text-xs text-muted-foreground">{inv.start_date} to {inv.end_date} &middot; {inv.total_visits} visits &middot; {inv.total_hours} hrs</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <p className="font-heading text-xl font-semibold text-green-700">${inv.total_amount.toFixed(2)}</p>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => handleViewInvoice(inv.id)} data-testid={`view-invoice-${inv.id}`}>
                        <Eye weight="bold" className="w-4 h-4" />
                      </Button>
                      {!isClient && inv.status === 'draft' && (
                        <Button variant="ghost" size="sm" className="rounded-xl text-destructive hover:text-destructive" onClick={() => handleDelete(inv.id)} data-testid={`delete-invoice-${inv.id}`}>
                          <Trash weight="bold" className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      </>)}
    </div>
  );
};

export default InvoicesPage;
